// Clinic portal — submit a case. Four steps: Patient → Case details → Records → Review.
//
// The Records step is the point of the whole screen: named slots, not a generic dropzone, so the
// doctor can see at a glance which records are still missing before they send the case.
// Files upload immediately against the created case, so a slow STL never blocks the submit button.
import { TurnaroundPicker } from '../../ui/TurnaroundPicker.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WF_ARCH_LABELS,
  turnaroundOf,
  turnaroundSurchargeRupees,
  WF_REQ_LABELS,
  type WfArch,
  type WfPortalCase,
  type WfPortalMeta,
  type WfPortalSubmit,
  type WfRecordSlot,
} from '@alignova/shared';
import { ApiError } from '../../lib/api.ts';
import { WizardFooter, WizardStepper } from '../../ui/Wizard.tsx';
import { portalApi, uploadPortalRecord, isPortalArchiveOutcome } from './portalApi.ts';

const STEPS = ['Patient', 'Case details', 'Records', 'Review & submit'] as const;

const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-[15px] text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';
const lbl = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted md:mb-0 md:pt-3';
// Horizontal on desktop: label left, control right. Stacks on mobile, where a phone-width screen
// has no room for a label column.
const row = 'block md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4';

/** A file the clinic has attached to a named slot, with its own upload state. */
interface SlotFile {
  slotKey: string;
  slotLabel: string;
  file: File;
  preview: string | null;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress?: number; // 0-100 while uploading
  extracted?: number; // files produced when the upload was a .zip/.rar
  error?: string;
}

export function SubmitCaseWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState<WfPortalMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [files, setFiles] = useState<SlotFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [f, setF] = useState<WfPortalSubmit>({
    patientFirstName: '',
    patientLastName: '',
    caseTypes: [],
    priority: 'normal',
    iprStatus: 'not_required',
    etchingStatus: 'not_required',
  });
  const set = <K extends keyof WfPortalSubmit>(k: K, v: WfPortalSubmit[K]) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    portalApi.meta().then(setMeta).catch((e) => setMetaError(e instanceof Error ? e.message : String(e)));
  }, []);
  // Object URLs for image previews must be revoked or the tab leaks memory on a long intake session.
  useEffect(() => () => files.forEach((x) => x.preview && URL.revokeObjectURL(x.preview)), [files]);

  /**
   * Two case types, then "More".
   *
   * The lab configures a dozen of them and the list pushed everything else on this step below the
   * fold on a phone. The first two are whatever the lab put first, so the common ones stay one tap
   * away; the rest are one tap further. Anything already chosen stays visible whether or not the
   * list is expanded — a selection that scrolls out of sight when you collapse a list reads as
   * having been thrown away.
   */
  const [allTypes, setAllTypes] = useState(false);
  const caseTypeOptions = meta?.caseTypes ?? [];
  const shownTypes = useMemo(
    () => (allTypes ? caseTypeOptions : caseTypeOptions.filter((t, i) => i < 2 || f.caseTypes.includes(t.key))),
    [allTypes, caseTypeOptions, f.caseTypes],
  );
  const hiddenTypeCount = caseTypeOptions.length - shownTypes.length;

  // Both halves of the name are required, so the step cannot be left on a first name alone.
  const nameOk = f.patientFirstName.trim().length > 0 && (f.patientLastName ?? '').trim().length > 0;
  const typesOk = f.caseTypes.length > 0;
  const canLeaveStep = step === 0 ? nameOk : step === 1 ? typesOk : true;

  const attach = useCallback((slot: WfRecordSlot, file: File) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setFiles((prev) => {
      const old = prev.find((x) => x.slotKey === slot.key);
      if (old?.preview) URL.revokeObjectURL(old.preview);
      return [...prev.filter((x) => x.slotKey !== slot.key), { slotKey: slot.key, slotLabel: slot.label, file, preview, status: 'pending' }];
    });
  }, []);
  const detach = (slotKey: string) =>
    setFiles((prev) => {
      const old = prev.find((x) => x.slotKey === slotKey);
      if (old?.preview) URL.revokeObjectURL(old.preview);
      return prev.filter((x) => x.slotKey !== slotKey);
    });

  async function submit() {
    if (!nameOk) {
      setError('The patient’s first and last name are both required');
      setStep(0);
      return;
    }
    if (!typesOk) {
      setError('Choose at least one case type');
      setStep(1);
      return;
    }
    setSubmitting(true);
    setError(null);
    let created: WfPortalCase;
    try {
      setProgress('Creating case…');
      created = await portalApi.submit(f);
    } catch (e) {
      setSubmitting(false);
      setProgress(null);
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
      return;
    }
    // The case exists now. Upload records one at a time and report per-file outcomes; a failed
    // record must not lose the case the doctor already submitted.
    let failed = 0;
    for (const sf of files) {
      setProgress(`Uploading ${sf.slotLabel}…`);
      setFiles((prev) => prev.map((x) => (x.slotKey === sf.slotKey ? { ...x, status: 'uploading', progress: 0 } : x)));
      try {
        const out = await uploadPortalRecord(created.id, sf.file, sf.slotKey, (pct) =>
          setFiles((prev) => prev.map((x) => (x.slotKey === sf.slotKey ? { ...x, progress: pct } : x))),
        );
        const extracted = isPortalArchiveOutcome(out) ? out.extracted.length : undefined;
        setFiles((prev) => prev.map((x) => (x.slotKey === sf.slotKey ? { ...x, status: 'done', progress: 100, extracted } : x)));
      } catch (e) {
        failed++;
        const msg = e instanceof Error ? e.message : String(e);
        setFiles((prev) => prev.map((x) => (x.slotKey === sf.slotKey ? { ...x, status: 'error', error: msg } : x)));
      }
    }
    setSubmitting(false);
    setProgress(null);
    if (failed > 0) {
      setError(`Case ${created.caseNumber} was submitted, but ${failed} record${failed > 1 ? 's' : ''} failed to upload. Open the case to retry.`);
      return;
    }
    nav(`/portal/cases/${created.id}?submitted=1`);
  }

  if (metaError) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-status-urgent-fg/30 bg-status-urgent-bg p-6 text-center">
        <p className="font-semibold text-status-urgent-fg">Can&apos;t open the submission form</p>
        <p className="mt-1 text-sm text-status-urgent-fg/90">{metaError}</p>
      </div>
    );
  }
  if (!meta) return <div className="p-10 text-center text-muted">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl pb-28">
      <header className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Submit a case</h1>
        <p className="mt-0.5 text-sm text-muted">
          {meta.clinicName ?? meta.doctorName} · Group ID <span className="font-mono font-semibold text-ink">{meta.doctorCode}</span>
        </p>
      </header>

      <WizardStepper steps={STEPS} step={step} onJump={setStep} />

      {error && (
        <div className="mb-4 rounded-lg border border-status-urgent-fg/30 bg-status-urgent-bg px-4 py-3 text-sm text-status-urgent-fg">{error}</div>
      )}

      <div className="rounded-xl border border-line bg-surface p-5 shadow-level-1 sm:p-6">
        {step === 0 && (
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-ink">Who is the patient?</h2>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              {/* One field, as a doctor would say the name out loud.
                  Splitting into first and last asks the person filling this in to decide which part
                  of "Muhammad Ahmad" is the surname - a question with no reliable answer here, and
                  one the lab does not need answered. The split happens on the way to storage. */}
              {/* Both parts, both required. The surname is what a case is filed and searched
                  under at the lab, so it is its own field rather than inferred from where the
                  spaces fall in a full name. */}
              <label className={row}>
                <span className={lbl}>Patient first name *</span>
                <input
                  className={input}
                  autoFocus
                  autoComplete="off"
                  value={f.patientFirstName}
                  onChange={(e) => set('patientFirstName', e.target.value)}
                  placeholder="e.g. Muhammad"
                />
              </label>
              <label className={row}>
                <span className={lbl}>Patient last name *</span>
                <input
                  className={input}
                  autoComplete="off"
                  value={f.patientLastName ?? ''}
                  onChange={(e) => set('patientLastName', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && nameOk && setStep(1)}
                  placeholder="e.g. Ahmad"
                />
              </label>
            </div>
            <details className="rounded-lg border border-line px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium text-ink">Contact details (optional)</summary>
              <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3">
                <label className={row}>
                  <span className={lbl}>Phone</span>
                  <input className={input} value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
                </label>
                <label className={row}>
                  <span className={lbl}>WhatsApp</span>
                  <input className={input} value={f.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value)} />
                </label>
              </div>
            </details>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-5">
            <h2 className="text-lg font-semibold text-ink">What do you need made?</h2>
            <div>
              <span className={lbl}>Case type * - choose all that apply</span>
              <div className="flex flex-wrap gap-2">
                {shownTypes.map((t) => {
                  const on = f.caseTypes.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => set('caseTypes', on ? f.caseTypes.filter((x) => x !== t.key) : [...f.caseTypes, t.key])}
                      className={`tap rounded-full border px-3.5 py-2 text-sm font-medium transition ${on ? 'border-primary bg-primary text-primary-fg' : 'border-line text-ink hover:bg-surface-2'}`}
                    >
                      {t.label}
                    </button>
                  );
                })}

                {!allTypes && hiddenTypeCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setAllTypes(true)}
                    className="tap rounded-full border border-dashed border-line px-3.5 py-2 text-sm font-medium text-accent transition hover:bg-surface-2"
                  >
                    More ({hiddenTypeCount})
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <label className={row}>
                <span className={lbl}>Arches</span>
                <select className={input} value={f.arch ?? ''} onChange={(e) => set('arch', (e.target.value || null) as WfArch | null)}>
                  <option value="">Not specified</option>
                  {meta.arches.map((a) => (
                    <option key={a} value={a}>
                      {WF_ARCH_LABELS[a]}
                    </option>
                  ))}
                </select>
              </label>
              <label className={row}>
                <span className={lbl}>New case or refinement</span>
                <select className={input} value={f.newOrRefinement ?? ''} onChange={(e) => set('newOrRefinement', (e.target.value || null) as 'new' | 'refinement' | null)}>
                  <option value="">Not specified</option>
                  <option value="new">New case</option>
                  <option value="refinement">Refinement</option>
                </select>
              </label>
            </div>
            {/* The turnaround IS the delivery date, so there is no separate date field to disagree
                with it. Express states its surcharge before the doctor commits to it. */}
            <TurnaroundPicker
              className="mt-1"
              value={f.priority ?? 'normal'}
              onChange={(p) => set('priority', p)}
            />
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <label className={row}>
                <span className={lbl}>IPR</span>
                <select className={input} value={f.iprStatus} onChange={(e) => set('iprStatus', e.target.value as WfPortalSubmit['iprStatus'])}>
                  <option value="not_required">{WF_REQ_LABELS.not_required}</option>
                  <option value="required">{WF_REQ_LABELS.required}</option>
                </select>
              </label>
              <label className={row}>
                <span className={lbl}>Etching guide</span>
                <select className={input} value={f.etchingStatus} onChange={(e) => set('etchingStatus', e.target.value as WfPortalSubmit['etchingStatus'])}>
                  <option value="not_required">{WF_REQ_LABELS.not_required}</option>
                  <option value="required">{WF_REQ_LABELS.required}</option>
                </select>
              </label>
            </div>
            <label className={row}>
              <span className={lbl}>Notes / instructions for the lab</span>
              <textarea className={input} rows={3} value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} placeholder="Anything the technician should know" />
            </label>
          </section>
        )}

        {step === 2 && <RecordsStep meta={meta} files={files} onAttach={attach} onDetach={detach} />}

        {step === 3 && <ReviewStep f={f} meta={meta} files={files} onEdit={setStep} />}
      </div>

      <WizardFooter
        step={step}
        last={STEPS.length - 1}
        canContinue={canLeaveStep}
        busy={submitting}
        progress={progress}
        variant="fixed"
        submitLabel={submitting ? 'Submitting…' : 'Submit case'}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onNext={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
        onSubmit={() => void submit()}
      />
    </div>
  );
}


function RecordsStep({
  meta,
  files,
  onAttach,
  onDetach,
}: {
  meta: WfPortalMeta;
  files: SlotFile[];
  onAttach: (slot: WfRecordSlot, file: File) => void;
  onDetach: (slotKey: string) => void;
}) {
  const byKey = useMemo(() => new Map(files.map((x) => [x.slotKey, x])), [files]);
  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Attach the records</h2>
        <p className="mt-0.5 text-sm text-muted">
          Tap a tile to attach that record. Empty tiles show what&apos;s still missing - nothing here is mandatory, but the more you send, the fewer questions come back.
        </p>
      </div>
      {meta.recordSlots.map((group) => (
        <div key={group.key}>
          <div className="mb-2 flex items-baseline gap-2">
            <h3 className="text-sm font-semibold text-ink">{group.label}</h3>
            <span className="text-xs text-muted">{group.hint}</span>
            <span className="ml-auto text-xs font-medium text-muted">
              {group.slots.filter((s) => byKey.has(s.key)).length}/{group.slots.length}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4">
            {group.slots.map((slot) => (
              <SlotTile key={slot.key} slot={slot} attached={byKey.get(slot.key)} onAttach={onAttach} onDetach={onDetach} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function SlotTile({
  slot,
  attached,
  onAttach,
  onDetach,
}: {
  slot: WfRecordSlot;
  attached?: SlotFile;
  onAttach: (slot: WfRecordSlot, file: File) => void;
  onDetach: (slotKey: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const ext = attached ? (attached.file.name.split('.').pop() ?? '').toUpperCase() : '';
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) onAttach(slot, file);
        }}
        className={`relative grid aspect-[4/3] place-items-center overflow-hidden rounded-lg border-2 border-dashed transition ${
          over ? 'border-accent bg-accent/10' : attached ? 'border-solid border-status-progress-fg/40 bg-status-progress-bg/30' : 'border-line bg-surface-2/50 hover:border-accent/60 hover:bg-surface-2'
        }`}
      >
        {attached ? (
          attached.preview ? (
            <img src={attached.preview} alt={slot.label} className="h-full w-full object-cover" />
          ) : (
            <span className="font-mono text-sm font-bold text-status-progress-fg">{ext || 'FILE'}</span>
          )
        ) : (
          <span className="text-2xl text-muted/50">+</span>
        )}
        {attached && (
          <span className="absolute right-1 top-1 flex items-center gap-1">
            {attached.status === 'uploading' && <span className="rounded bg-surface/90 px-1 text-[10px] font-semibold text-ink">{attached.progress ?? 0}%</span>}
            {attached.status === 'done' && <span className="rounded bg-status-progress-fg px-1 text-[10px] font-bold text-white">✓{attached.extracted ? ` ${attached.extracted}` : ''}</span>}
            {attached.status === 'error' && <span className="rounded bg-status-urgent-fg px-1 text-[10px] font-bold text-white">!</span>}
          </span>
        )}
      </button>
      <div className="flex items-start gap-1">
        <span className="flex-1 text-[11px] font-medium leading-tight text-ink">{slot.label}</span>
        {attached && (
          <button type="button" onClick={() => onDetach(slot.key)} className="text-[11px] text-muted hover:text-status-urgent-fg" aria-label={`Remove ${slot.label}`}>
            ✕
          </button>
        )}
      </div>
      {attached?.error && <span className="text-[10px] text-status-urgent-fg">{attached.error}</span>}
      {attached?.status === 'uploading' && (
        <div className="h-1 w-full overflow-hidden rounded bg-surface-2"><div className="h-full bg-accent transition-all" style={{ width: `${attached.progress ?? 0}%` }} /></div>
      )}
      {attached && (ext === 'ZIP' || ext === 'RAR') && attached.status !== 'done' && (
        <span className="text-[10px] font-semibold text-muted">🗜 {ext} - will be extracted</span>
      )}
      <input ref={ref} type="file" accept=".stl,.ply,.obj,.3mf,.dcm,.jpg,.jpeg,.png,.gif,.webp,.heic,.bmp,.tif,.tiff,.mp4,.mov,.webm,.pdf,.doc,.docx,.txt,.xml,.csv,.xlsx,.zip,.rar" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) onAttach(slot, file); e.target.value = ''; }} />
    </div>
  );
}

function ReviewStep({ f, meta, files, onEdit }: { f: WfPortalSubmit; meta: WfPortalMeta; files: SlotFile[]; onEdit: (s: number) => void }) {
  const typeLabel = (k: string) => meta.caseTypes.find((t) => t.key === k)?.label ?? k;
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div className="flex justify-between gap-4 border-b border-line py-2 last:border-0">
      <span className="text-sm text-muted">{k}</span>
      <span className="text-right text-sm font-medium text-ink">{v || <span className="text-muted">-</span>}</span>
    </div>
  );
  return (
    <section className="space-y-5">
      <h2 className="text-lg font-semibold text-ink">Check and send</h2>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Patient</h3>
          <button className="text-xs text-accent hover:underline" onClick={() => onEdit(0)}>Edit</button>
        </div>
        <Row k="Name" v={`${f.patientFirstName} ${f.patientLastName ?? ''}`.trim()} />
        <Row k="Contact" v={[f.phone, f.whatsapp].filter(Boolean).join(' · ')} />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Case</h3>
          <button className="text-xs text-accent hover:underline" onClick={() => onEdit(1)}>Edit</button>
        </div>
        <Row k="Case type" v={f.caseTypes.map(typeLabel).join(', ')} />
        <Row k="Arches" v={f.arch ? WF_ARCH_LABELS[f.arch] : ''} />
        <Row k="New / refinement" v={f.newOrRefinement} />
        <Row k="Delivery" v={`${turnaroundOf(f.priority).label} - ${turnaroundOf(f.priority).days} days`} />
        {turnaroundSurchargeRupees(f.priority) > 0 && (
          <Row
            k="Express charge"
            v={`PKR ${turnaroundSurchargeRupees(f.priority).toLocaleString('en-PK')} extra`}
          />
        )}
        <Row k="IPR" v={f.iprStatus === 'required' ? 'Required' : ''} />
        <Row k="Etching guide" v={f.etchingStatus === 'required' ? 'Required' : ''} />
        <Row k="Notes" v={f.notes} />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Records ({files.length})</h3>
          <button className="text-xs text-accent hover:underline" onClick={() => onEdit(2)}>Edit</button>
        </div>
        {files.length ? (
          <ul className="flex flex-wrap gap-1.5">
            {files.map((x) => (
              <li key={x.slotKey} className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-ink">
                {x.slotLabel} <span className="text-muted">· {(x.file.size / 1024 / 1024).toFixed(1)} MB</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            No records attached. You can still submit - the lab will request what it needs, which usually delays the case.
          </p>
        )}
      </div>
      <p className="text-xs text-muted">
        Submitting sends this to {meta.clinicName ?? 'the lab'} under Group ID <span className="font-mono">{meta.doctorCode}</span> and starts the workflow. You&apos;ll be able to track its progress.
      </p>
    </section>
  );
}

